// Copyright 2024 Sorama B.V.

import { autoinject, BindingEngine, computedFrom, Disposable, observable, transient } from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { AnalysisWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/AnalysisWorkflowModuleCatalog";
import {
    BarBottomLocation,
    BarData,
    BarDataDisplayOptions,
    BarTextLocation,
    Boundary,
    LineData,
    PlotData,
    PlotDataTypes,
} from "controls/plot/graph-plot/GraphPlot";
import { Range as PlotRange } from "controls/plot/IPlotParent";
import { Annotation } from "controls/plot/parts/AnnotationPart";
import { LegendEntry, LegendGroup } from "controls/plot/parts/LegendPart";
import { UnitValueToStringValueConverter } from "converters/UnitValueToStringValueConverter";
import {
    AxisKind,
    FrequencyAxisKinds,
    LinearBarkBandAxisKind,
    LinearCriticalBandAxisKind,
    LinearFrequencyAxisKind,
} from "datatypes/SettingsKinds/AxisKinds";
import { FrequencyBandKind, FrequencyBandKinds } from "datatypes/SettingsKinds/FrequencyBandKinds";
import { GenericMeasurement, Point2D, Point3D, PsychoAcousticsResults, Range } from "datatypes/SoramaWebApi";
import {
    UnitSpectrumWithMeasurement,
    WeightedSpectrumWithMeasurement,
} from "datatypes/WeightedSpectrumWithMeasurement";
import { FileNameConversion } from "extensions/FileNameConversion";
import { IExportable } from "libraries/Export/IExportable";
import { BandValue, LinearFrequencyBand, OctaveBands } from "libraries/Math/OctaveBands";

import { MeasurementField, MeasurementRepository } from "repositories/MeasurementRepository";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";

import { Frequency } from "units/Frequency";
import { Pressure } from "units/Pressure";
import { CriticalBandUnit, Unit, UnitValue, WeightedUnit } from "units/Unit";
import { UnitSpectrum } from "units/UnitSpectrum";
import { WeightedSpectrum } from "units/WeightedSpectrum";
import * as CriticalBandRangeConverter from "./CriticalBandRangeConverter";

import { IToastError } from "controls/toast/Toast";
import { MeasurementExtensions } from "extensions/MeasurementExtensions";
import { ObjectExtensions } from "extensions/ObjectExtensions";
import { Chameleon } from "libraries/Chameleon/Chameleon";
import {
    IWorkflowModuleExportData,
    IWorkflowModuleExportVisual,
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
    IWorkflowModuleModalInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import {
    WorkflowPropertyBindingMode,
    WorkflowPropertyScope,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { userSetting } from "libraries/WorkflowFramework/UserSettingsAttribute";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { AnalysisPresetType } from "modules/modals/AnalysisPreset/AnalysisPresets";
import { CSVExportModalState } from "modules/modals/CSVExport/CSVExportModal";
import { ExportModal, ExportModalState, IExportModule } from "modules/modals/Export/ExportModal";
import { FrequencyBandInformationModalState } from "modules/modals/FrequencyBandInformation/FrequencyBandInformationModal";
import { ImageExportState, LogoPositions } from "modules/modals/ImageExport/ImageExportModal";
import { SettingsModalState } from "modules/modals/SettingsModal";
import { AxisKindParser } from "parsers/AxisKindParser";
import { WeightedPressureUnitParser } from "parsers/WeightedPressureUnitParser";
import { MeasurementColorService } from "services/Color/MeasurementColorService";
import { FrequencySelectionService, SelectableRange } from "services/FrequencySelection/FrequencySelectionService";
import { FrequencySuggestionService } from "services/FrequencySelection/FrequencySuggestionService";
import { HarmonicPeaksFinder } from "services/FrequencySelection/HarmonicPeaksFinder";
import { HardwareSampleRate } from "services/Hardware/HardwareSamplerRate";
import { NotificationUrgency } from "services/Notification/INotificationService";
import { RecommendationEngine } from "services/RecommendationEngine/RecommendationEngine";
import { IPsychoAcousticsProcessingService } from "services/SoundProcessing/IPsychoAcousticsProcessingService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";
import Sortable from "sortablejs";
import { BarkBand } from "units/BarkBand";
import { CriticalBand } from "units/CriticalBand";
import { PsychoAcousticUnits } from "units/PsychoAcousticsUnits";
import { NoneWeighting, ZWeighting } from "units/Weighting";
import { v4 as uuidv4 } from "uuid";
import { AnalysisWorkflowModuleHelpers } from "../AnalysisWorkflowModuleHelpers";
import {
    AllPsychoAcousticMetricTypes,
    Mapper,
    PsychoAcousticAnalysisResult,
    PsychoAcousticAnalysisResultPerMeasurement,
    PsychoAcousticBandUnits,
    PsychoAcousticMetricType,
} from "../PsychoAcoustics/SpiderChart/SpiderChart";
import { FrequencySpectrumProvider } from "./FrequencySpectrumProvider";
import { ISpectrumProvider } from "./ISpectrumProvider";
import { PsychoAcousticsSpectrumProvider } from "./PsychoAcousticSpectrumProvider";
import {
    ComputationMode,
    ComputationModes,
    HarmonicSelectionModes,
    NarrowbandSelectionModes,
} from "./SpectrumModuleModes";

class UnitSpectrumWithSelectedMeasurement extends UnitSpectrumWithMeasurement {
    public selectedFrequencyBandId?: string;
    public selectedFrequencyBandKind?: FrequencyBandKind = FrequencyBandKinds.None;
}

interface SplView {
    name: string;
    powerValue: number;
    color: string;
    weighting: string;
}

@transient()
@autoinject
export class SpectrumModule
    implements IWorkflowModuleLifetime, IWorkflowModuleExportData, IWorkflowModuleExportVisual, IExportModule
{
    //#region Elements

    private dataExportable: IExportable;
    private draggableListElement: HTMLElement;

    private spectrumProvider: ISpectrumProvider;

    //#endregion

    //#region Constants

    // Default bounds within we show the frequency values
    private defaultBoundsFrequency: PlotRange = {
        from: new UnitValue(10, Frequency.hertz),
        // Get the nyquist frequency given the default sample rate
        to: new UnitValue(HardwareSampleRate.defaultCAMSampleRate / 2, Frequency.hertz),
    };

    // Default bounds of critical bands
    @computedFrom("variant")
    private get defaultBoundsCriticalBands(): PlotRange {
        if (PsychoAcousticBandUnits[this.variant] === BarkBand.averaged) {
            return {
                from: new UnitValue(1, BarkBand.averaged),
                to: new UnitValue(24, BarkBand.averaged),
            };
        } else {
            return {
                from: new UnitValue(1, CriticalBand.averaged),
                to: new UnitValue(26, CriticalBand.averaged),
            };
        }
    }

    public static UnitValueToStringValueConverter = new UnitValueToStringValueConverter();
    private moduleInstance: IWorkflowModuleInstance;
    private spectrumSettingsModal: IWorkflowModuleModalInstance<SettingsModalState<SpectrumModule>, void>;
    private exportModal: IWorkflowModuleModalInstance<ExportModal, void>;

    public imageState: { state: ImageExportState; moduleInstance: IWorkflowModuleInstance } = {
        state: null,
        moduleInstance: null,
    };
    public csvState: { state: CSVExportModalState; moduleInstance: IWorkflowModuleInstance } = {
        state: null,
        moduleInstance: null,
    };

    //#endregion

    //#region  Module properties
    @computedFrom("busy", "spectra", "spectra.length")
    public get disableExport(): boolean {
        return this.busy || !this.spectra || this.spectra.length == 0;
    }

    public presetType: AnalysisPresetType;

    public variant?: PsychoAcousticMetricType;

    //#endregion

    //#region View Fields
    @observable
    private spectra: PlotData[];

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, scope: WorkflowPropertyScope.workflow })
    private showLegend = false;

    public legendArray: LegendGroup[] = [];
    public recommendedFreqRange: PlotRange[] = [];

    public autoMinScaling = true;
    public autoMaxScaling = true;

    public ignoreMinYUpdate = true;
    @observable
    public minYAxis = 0;

    public ignoreMaxYUpdate = true;
    @observable
    public maxYAxis = 1;

    public powerValueView: SplView[] = [];

    public powerValue: number[] = [];

    public splMessage: string;

    private freqModal: IWorkflowModuleModalInstance<unknown, unknown>;
    public computeProgress: number;
    public computeProgressMessage: string;

    public isFullScreen = false;
    //#endregion

    //#region State Fields
    private busy: boolean;

    private suggestionsInitialized: boolean;

    // The Unit spectra as received from the API call.
    private currentUnitSpectra: UnitSpectrumWithSelectedMeasurement[];
    private currentPsychoAcousticResults: Partial<PsychoAcousticAnalysisResult>[];

    // Loading spinner
    public progress = 0;
    private subProgress = [];

    // Collection of the analysis results, containing microphone properties, and psychoacoustic values.
    public computedResults: PsychoAcousticAnalysisResultPerMeasurement = {};

    // Module properties
    @computedFrom("variant", "selectedMeasurementIds")
    public get metricUnit(): string {
        return this.variant ? PsychoAcousticUnits.fromString(this.variant).unit : "";
    }

    // The current converted weighted Spectra of the unit spectra
    private currentWeightedSpectra: WeightedSpectrumWithMeasurement[];

    private measurements: GenericMeasurement[] = [];

    private disposables: Disposable[] = [];
    private colorObserverDisposables: Disposable[] = [];
    private measurementUpdateDisposables: Disposable[] = [];

    //#endregion

    //#region Workflow Properties
    private oldSelectedFrequencies: SelectableRange[];

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public isFrequencyRangeLinked: boolean;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public frequencyRange: Range;

    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        objectType: "string",
        changedHandler: "selectedMeasurementIdsChanged",
    })
    public selectedMeasurementIds: string[] = [];

    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        objectType: "string",
        changedHandler: "selectedMeasurementIdsChanged",
    })
    public selectedPsychoAcousticMeasurementIds: string[] = [];

    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        objectType: "Point3D",
        changedHandler: "selectedMeasurementIdsChanged",
    })
    public selectedMic: Point3D;
    //#endregion

    //#region Settings
    @observable({ changeHandler: "xUnitRangeInputChanged" })
    private minDisplayedXAxisValue: UnitValue;
    @observable({ changeHandler: "xUnitRangeInputChanged" })
    private maxDisplayedXAxisValue: UnitValue;

    // Placeholders that will be overwritten immediately.
    private minXAxis: number = this.defaultBoundsFrequency.from.value;
    private maxXAxis: number = this.defaultBoundsFrequency.to.value;

    private get displayXAxisUnit(): Unit {
        if (this.variant) {
            return PsychoAcousticBandUnits[this.variant];
        } else {
            return this.minDisplayedXAxisValue?.unit ?? Frequency.hertz;
        }
    }

    // Default is set to frequency
    displayYAxisUnit: Unit = Pressure.dbSPL;

    // This static and get structure is done on purpose such that it's not bindable.
    private static AvailableUnits = [Pressure.dbSPL, Pressure.dbASPL, Pressure.dbBSPL, Pressure.dbCSPL];
    private get availableUnits(): WeightedUnit[] {
        return this.variant ? [] : SpectrumModule.AvailableUnits;
    }
    public annotations: Annotation[];
    public frequencyBandKinds: FrequencyBandKind[] = FrequencyBandKinds.All();
    public narrowBandSelectionModes: string[] = NarrowbandSelectionModes.all;

    @observable
    @userSetting({ parser: new AxisKindParser() })
    private selectedFrequencyAxisKind: AxisKind = LinearFrequencyAxisKind;
    public frequencyAxisKinds = FrequencyAxisKinds;

    @userSetting()
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private narrowBandSelectionMode: string = NarrowbandSelectionModes.maxBand;

    @observable
    @userSetting({ parser: new WeightedPressureUnitParser() })
    private selectedUnit: WeightedUnit;

    public harmonicSelectionModes: string[] = HarmonicSelectionModes.all;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private harmonicSelectionMode: string = HarmonicSelectionModes.none;

    private higherHarmonicsColor = "green";
    private lowerHarmonicsColor = "blue";

    public computationModes: ComputationMode[] = ComputationModes.all;

    @observable
    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        changedHandler: "selectedMeasurementIdsChanged",
    })
    private computationMode: ComputationMode = ComputationModes.normal;

    private get weightingLowerBound(): UnitValue {
        return new UnitValue(ComputationModes.getValidFrequencyRange(this.computationMode).from, Frequency.hertz);
    }

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, objectType: "selectableRange" })
    public frequencyRanges: SelectableRange[] = [];

    protected criticalBandRanges: SelectableRange[] = [];

    // Id used to target this specific module in the DOM
    protected uniqueModuleId: string = uuidv4();

    // Preserves the cancellation state of the previous updateData call
    private updateDataAbortController: AbortController;

    //#endregion

    //#region Constructor and Initialization
    constructor(
        private readonly processorResolver: IMeasurementProcessorResolver,
        private readonly measurementRepository: MeasurementRepository,
        private readonly measurementColorService: MeasurementColorService,
        private readonly bindingEngine: BindingEngine,
        private readonly workflowModeService: WorkflowModeService,
        private readonly frequencySelectionService: FrequencySelectionService,
        private readonly frequencySuggestionService: FrequencySuggestionService
    ) {}

    public bind(): void {
        this.disposables.push(
            this.bindingEngine.propertyObserver(this.frequencySelectionService, "selectedFrequency").subscribe(() => {
                this.selectedFrequencyChanged(this.frequencySelectionService.selectedFrequency);
            }),
            this.frequencySelectionService.subscribeToRangeChanges((rangePropertiesChanged) => {
                if (!rangePropertiesChanged) {
                    this.selectedFrequenciesChanged(this.frequencySelectionService.ranges);
                }
                this.updateNarrowBandSelectionMode();
            })
        );
    }

    public attached(): void {
        if (this.draggableListElement) {
            Sortable.create(this.draggableListElement, {
                handle: ".grip-icon",
                animation: 150, // duration in ms
                onEnd: (event) => {
                    const movedItem = this.currentUnitSpectra.splice(event.oldIndex, 1)[0];
                    this.currentUnitSpectra.splice(event.newIndex, 0, movedItem);

                    this.computeSpectra();
                    this.updateLegendArray();
                },
            });
        }
    }

    public deactivateModule(): void {
        this.frequencySelectionService.unregisterModule(this.uniqueModuleId);

        while (this.measurementUpdateDisposables.length > 0) {
            this.measurementUpdateDisposables.pop()?.dispose();
        }

        while (this.colorObserverDisposables.length > 0) {
            this.colorObserverDisposables.pop()?.dispose();
        }

        while (this.disposables.length > 0) {
            this.disposables.pop()?.dispose();
        }
    }

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        if (this.moduleInstance.variant) {
            if (AllPsychoAcousticMetricTypes.includes(this.moduleInstance.variant as PsychoAcousticMetricType)) {
                this.variant = this.moduleInstance.variant as PsychoAcousticMetricType;
            } else {
                // TODO-SUS-514: Use workflowmoduleinstance to show a toast message when an error is thrown
                // This should not be the user's fault, it means in module instantiation code we passed an invalid string
                throw new Error(
                    "Invalid variant for Spectrum Module: " +
                        this.moduleInstance.variant +
                        ". It is not a PsychoAcoustic Metric Type."
                );
            }
        }

        this.spectrumProvider = new FrequencySpectrumProvider(this.processorResolver);
        if (this.variant) {
            this.moduleInstance.title = `${this.variant} - Critical Bands`;
            this.spectrumProvider = new PsychoAcousticsSpectrumProvider(this.processorResolver, this.variant);

            // Set default bounds.
            this.minDisplayedXAxisValue = this.defaultBoundsCriticalBands.from;
            this.maxDisplayedXAxisValue = this.defaultBoundsCriticalBands.to;
            this.minXAxis = this.minDisplayedXAxisValue.value;
            this.maxXAxis = this.maxDisplayedXAxisValue.value;

            // Set scaling
            this.selectedFrequencyAxisKind = LinearCriticalBandAxisKind;
            if (PsychoAcousticBandUnits[this.variant] === BarkBand.averaged) {
                this.selectedFrequencyAxisKind = LinearBarkBandAxisKind;
            }

            // Set YAxis value (also the visible one)
            this.displayYAxisUnit = PsychoAcousticUnits.fromString(this.variant);

            // Linking frequency range does not behave correctly when going between critical band and hertz
            this.isFrequencyRangeLinked = false;
        } else {
            this.minDisplayedXAxisValue = this.defaultBoundsFrequency.from;
            this.maxDisplayedXAxisValue = this.defaultBoundsFrequency.to;
        }

        if (!this.selectedUnit) {
            this.selectedUnit = this.availableUnits[0];
        }

        this.determineDefaultComputationMode();

        const initialFrequencyRanges =
            this.frequencyRanges && this.frequencyRanges.length > 0
                ? this.frequencyRanges
                : this.frequencySelectionService.ranges;
        this.selectedFrequenciesChanged(initialFrequencyRanges);

        this.selectedMeasurementIdsChanged();
    }

    private determineDefaultComputationMode(): void {
        // When in iV64 only mode (or iv64-ex), always change to iV64 computation mode
        if (
            this.workflowModeService.selectedWorkflowMode.canUseHandheld &&
            !this.workflowModeService.selectedWorkflowMode.canUseCAM64AndCAM1K
        ) {
            this.computationMode = ComputationModes.iV64;
            return;
        }
        // When in CAM64 and CAM1K only mode, always change to normal (High-res) computation mode
        if (
            !this.workflowModeService.selectedWorkflowMode.canUseHandheld &&
            this.workflowModeService.selectedWorkflowMode.canUseCAM64AndCAM1K
        ) {
            this.computationMode = ComputationModes.normal;
            return;
        }

        // When in all products or anything where cam type used is not certain, don't change anything
    }
    //#endregion

    //#region Change callbacks
    public selectedFrequenciesChanged(
        newRanges: SelectableRange[],
        old?: SelectableRange[],
        propertyName?: string,
        force?: boolean
    ): void {
        // We need splice to trigger the binding engine
        this.frequencyRanges.splice(0, this.frequencyRanges.length, ...newRanges);

        // If this is a critical band spectrum, also update the critical band ranges based on the new Hz ranges
        if (this.variant && this.displayXAxisUnit instanceof CriticalBandUnit) {
            const newCriticalBandRanges = CriticalBandRangeConverter.hzRangesToCriticalBandRanges(
                newRanges || [],
                this.displayXAxisUnit as CriticalBandUnit
            );
            this.criticalBandRanges.splice(0, this.criticalBandRanges.length, ...newCriticalBandRanges);
        }

        if (!ObjectExtensions.deepEqual(this.frequencySelectionService.ranges, newRanges)) {
            this.frequencySelectionService.ranges.splice(0, this.frequencySelectionService.ranges.length, ...newRanges);
        }

        const oldValue = this.oldSelectedFrequencies;
        this.oldSelectedFrequencies = newRanges ? [...newRanges] : undefined;

        if (!oldValue) {
            // The first change is always triggered by the binding engine, ignore this change to prevent the selected frequency being unset
            return;
        }

        if (this.frequencySelectionService.ranges.length > 0) {
            Chameleon.setObservable("spectrum", true);
        } else {
            Chameleon.setObservable("spectrum", false);
        }
        Chameleon.checkObservable("spectrum");

        this.activeRangesChanged();

        if (!force) {
            if (!newRanges) {
                return;
            }

            if (oldValue && oldValue.length !== 0 && newRanges.length == oldValue.length) {
                let equal = true;
                for (let i = 0; i < newRanges.length; i++) {
                    if (
                        newRanges[i].from !== oldValue[i].from ||
                        newRanges[i].to !== oldValue[i].to ||
                        newRanges[i].isSelected !== oldValue[i].isSelected
                    ) {
                        equal = false;
                    }

                    if (!equal) break;
                }

                if (equal) return;
            }
        }
        this.updateNarrowBandSelectionMode();
    }

    // Called when the user selects a range on the plot's x axis
    protected selectionUpdated(selection: SelectableRange[]): void {
        this.frequencySelectionService.setActiveModule(this.uniqueModuleId);

        if (this.busy) return;
        // No need to do anything on the selection for a normal spectrum
        if (!this.variant) return;
        if (selection?.length > 0) {
            // Open far field for each measurement if not already
            this.openFarFieldModuleForCriticalBand();
        }

        // This is a critical band spectrum, so also update the Hz ranges based on the new critical band ranges
        const newFrequencyRangesHz = CriticalBandRangeConverter.criticalBandRangesToHzRanges(
            this.criticalBandRanges,
            this.displayXAxisUnit as CriticalBandUnit
        );
        this.frequencySelectionService.ranges.splice(
            0,
            this.frequencySelectionService.ranges.length,
            ...newFrequencyRangesHz
        );
    }

    public selectedFrequencyChanged(newFrequency?: number): void {
        if (newFrequency === undefined || newFrequency === null) {
            this.annotations = undefined;
            return;
        }

        this.annotations = [
            {
                x: new UnitValue(newFrequency, Frequency.hertz),
                color: "white",
                text: newFrequency.toFixed(0) + " " + Frequency.hertz.unit,
            },
        ];

        if (this.harmonicSelectionMode == HarmonicSelectionModes.none) return;

        //Continue with calculating harmonics since
        const increment = 1;

        const harmonics = HarmonicPeaksFinder.findHarmonicPeaks(
            this.spectra,
            newFrequency,
            this.minDisplayedXAxisValue.value,
            this.maxDisplayedXAxisValue.value,
            increment,
            this.harmonicSelectionMode
        );

        this.annotations.push(
            ...harmonics.map((h) => {
                return {
                    x: h.x,
                    color: h.harmonic > 0 ? this.higherHarmonicsColor : this.lowerHarmonicsColor,
                    text: h.x.value.toFixed(0) + " " + Frequency.hertz.unit,
                    harmonic: h.harmonic,
                };
            })
        );
    }

    public narrowBandSelectionModeChanged(): void {
        this.selectedFrequenciesChanged(this.frequencySelectionService.ranges, undefined, undefined, true);
    }

    public harmonicSelectionModeChanged(): void {
        this.selectedFrequencyChanged(this.frequencySelectionService.selectedFrequency);
    }

    public frequencyRangeChanged(newRange: Range): void {
        if (this.isFrequencyRangeLinked && newRange) {
            this.minDisplayedXAxisValue.value = Math.max(newRange.from, this.minDisplayedXAxisValue.value);
            this.maxDisplayedXAxisValue.value = Math.min(newRange.to, this.maxDisplayedXAxisValue.value);
        }
    }

    public xUnitRangeInputChanged(): void {
        this.frequencyRange = {
            from: this.minXAxis,
            to: this.maxXAxis,
        };
    }

    public isFrequencyRangeLinkedChanged(): void {
        if (this.variant) return;

        // Trigger update
        this.frequencyRangeChanged(this.frequencyRange);
    }

    public selectedUnitChanged(): void {
        this.displayYAxisUnit = this.selectedUnit;
        this.computeSpectra(true);
    }

    public selectedFrequencyAxisKindChanged(): void {
        this.computeSpectra(true);
    }

    public selectedMeasurementIdsChanged(): void {
        // Start the loading spinner
        this.busy = true;

        // If this is a psychoAcoustic time analysis module, get the psychoAcoustic measurement ids instead
        if (this.variant != null && this.selectedMeasurementIds !== this.selectedPsychoAcousticMeasurementIds) {
            this.selectedMeasurementIds = this.selectedPsychoAcousticMeasurementIds;
        }

        if (this.workflowModeService.selectedWorkflowMode.isIndustrial && this.suggestionsInitialized) {
            // Reset suggestions if we add/remove a measurement to the module (after the initial one)
            this.frequencySelectionService.ranges.splice(0, this.frequencySelectionService.ranges.length);
        }

        void this.updateData();

        // Make sure if we remove a measurement to update the narrow band selection
        this.updateNarrowBandSelectionMode();
    }

    private measurementUpdated = (property: MeasurementField, measurement: GenericMeasurement): void => {
        // Update measurement fields
        for (const m of this.measurements) {
            if (MeasurementExtensions.updateMeasurementField(m, measurement.id, property, measurement[property])) {
                break;
            }
        }

        // Check if we need to invalidate some data
        if (property === MeasurementField.mutedSensors) {
            this.moduleInstance.dispatch(() => {
                let findFunction = () =>
                    this.currentUnitSpectra.findIndex((x) =>
                        MeasurementExtensions.isRelatedTo(x.measurement, measurement.id)
                    );
                let invalidIndex = findFunction();
                while (invalidIndex !== -1) {
                    this.currentUnitSpectra.splice(invalidIndex, 1);
                    invalidIndex = findFunction();
                }

                findFunction = () =>
                    this.currentWeightedSpectra.findIndex((x) =>
                        MeasurementExtensions.isRelatedTo(x.measurement, measurement.id)
                    );
                invalidIndex = findFunction();
                while (invalidIndex !== -1) {
                    this.currentWeightedSpectra.splice(invalidIndex, 1);
                    invalidIndex = findFunction();
                }

                return this.updateData();
            });
        }
    };

    public maxYAxisChanged(newValue: number, oldValue: number): void {
        // Sometimes this function triggers with a very small update due to rounding errors, ignore those updates
        if (Math.abs(newValue - oldValue) < 1e-5) return;

        if (this.ignoreMaxYUpdate) {
            this.ignoreMaxYUpdate = false;
            return;
        }

        this.autoMaxScaling = false;
    }

    public minYAxisChanged(newValue: number, oldValue: number): void {
        // Sometimes this function triggers with a very small update due to rounding errors, ignore those updates
        if (Math.abs(newValue - oldValue) < 1e-5) return;

        if (this.ignoreMinYUpdate) {
            this.ignoreMinYUpdate = false;
            return;
        }

        this.autoMinScaling = false;
    }

    public activeRangesChanged(): void {
        this.updateNarrowBandSelectionMode();

        if (this.freqModal) {
            const state = {
                spectra: this.currentWeightedSpectra,
                frequencyRanges: this.frequencySelectionService.ranges,
            };

            this.freqModal.updateState(state);
        }
    }

    protected async openFrequencyModal() {
        if (this.freqModal && this.freqModal.isOpen) {
            this.freqModal.cancel();
            this.freqModal = undefined;
            return;
        }

        const state = {
            spectra: this.currentWeightedSpectra,
            frequencyRanges: this.frequencySelectionService.ranges,
        };

        this.freqModal = await this.moduleInstance
            .createModal<FrequencyBandInformationModalState>(WorkflowModalCatalog.FrequencyBandInformationModal)
            .withState(state)
            .whenClosed(() => {
                this.freqModal = undefined;
            })
            .show();
    }

    //#endregion

    //#region Spectrum Methods

    public initializeAnalysisPreset(presetType: AnalysisPresetType): void {
        if (this.workflowModeService.selectedWorkflowMode.isIndustrial) {
            this.presetType = presetType;

            // Opening new spectrum module from preset; clear selected frequency ranges to trigger recalculation of suggestions
            this.frequencySelectionService.ranges.splice(0, this.frequencySelectionService.ranges.length);
        }
    }

    public selectFrequencyBand(id: string, frequencyBandKind: FrequencyBandKind, index: number): void {
        this.busy = true;

        this.currentUnitSpectra[index].selectedFrequencyBandId =
            this.currentUnitSpectra[index].selectedFrequencyBandId === id &&
            this.currentUnitSpectra[index].selectedFrequencyBandKind === frequencyBandKind
                ? undefined
                : id;
        this.currentUnitSpectra[index].selectedFrequencyBandKind = frequencyBandKind;

        this.updateBands();

        this.busy = false;
    }

    private async updateData(dontRetry?: boolean): Promise<void> {
        // Cancel any ongoing updateData request
        if (this.updateDataAbortController) {
            this.updateDataAbortController.abort();
        }
        // Create a new controller for this request
        this.updateDataAbortController = new AbortController();
        const signal = this.updateDataAbortController.signal;

        // Waits for activateModule to be finished.
        if (!this.selectedMeasurementIds || !this.moduleInstance) return;

        this.busy = true;

        try {
            await this.syncMeasurements();

            if (signal.aborted) return;

            const progressPerMeasurement = new Array<number>(this.measurements.length);

            const spectraPromises: Promise<UnitSpectrumWithSelectedMeasurement>[] = [];

            const psychoAcousticsPromises: Promise<Partial<PsychoAcousticAnalysisResult>>[] = [];

            this.measurements.forEach((measurement, index) => {
                // Assign a color to the measurement
                this.measurementColorService.ensureAssignedColor({ measurementId: measurement.id });

                this.setMeasurementObservers(measurement);

                progressPerMeasurement[index] = 0;
                if (signal.aborted) return;

                spectraPromises.push(
                    this.getSpectra(measurement, (progress) => {
                        progressPerMeasurement[index] = progress;
                        const validProgresses = progressPerMeasurement.filter((x) => x !== undefined);
                        this.computeProgress =
                            5 + (validProgresses.reduce((a, b) => a + b, 0) / validProgresses.length) * 95;
                    })
                );

                if (this.variant && !this.computedResults[measurement.id]) {
                    psychoAcousticsPromises.push(this.getMeasurementPsychoAcoustics(index));
                } else {
                    this.setSubProgress(index, 1);
                }
            });

            // Check if the request was aborted
            if (signal.aborted) return;

            // Wait for all measurements to be computed
            this.currentPsychoAcousticResults = await Promise.all(psychoAcousticsPromises);

            this.computeProgressMessage = `Loading ${spectraPromises.length > 1 ? "spectra" : "spectrum"}`;

            // Check if the request was aborted
            if (signal.aborted) return;
            this.currentUnitSpectra = await Promise.all(spectraPromises);

            this.setMaxXAxisValue();

            this.computeSpectra();
            this.selectedFrequenciesChanged(
                this.frequencySelectionService.ranges,
                this.frequencySelectionService.ranges,
                undefined,
                true
            );
            this.selectedFrequencyChanged(this.frequencySelectionService.selectedFrequency);
            this.updateRecommendation(this.measurements);

            // Generate a timeline event
            void this.moduleInstance.createTimelineEvent();
        } catch (e) {
            // Only handle errors for not aborted requests
            if (signal.aborted) return;

            if ((e.statusCode === 0 || e.statusCode === 404) && !dontRetry) {
                // Check if the request was aborted
                if (signal.aborted) return;

                // Requeue the analysis
                return this.updateData(true);
            }

            if (e.message) {
                this.moduleInstance.createNotification().withUrgency(NotificationUrgency.Error).withMessage(e.message);
            }
        } finally {
            // Keep the busy state until all the promises are resolved
            this.busy = signal.aborted ? true : false;
        }
    }

    /**
     * Set the progress of a specific measurement
     */
    public setSubProgress(idx: number, progress: number) {
        this.subProgress[idx] = progress;
    }

    /**
     * Get the psychoacoustic results of a measurement
     * @param measurementIdx The index of the measurement in the measurements array
     * @returns The returned psychoacoustic object from the backend
     */
    public async getMeasurementPsychoAcoustics(measurementIdx: number): Promise<Partial<PsychoAcousticAnalysisResult>> {
        const { id: measurementId, name } = this.measurements[measurementIdx];

        const mic = this.selectedMic;
        if (mic == null) return;

        try {
            const soundProcessingService =
                await this.processorResolver.resolveProcessor<IPsychoAcousticsProcessingService>(
                    IPsychoAcousticsProcessingService,
                    this.measurements[measurementIdx]
                );

            // Send request to retrieve psychoacoustic analysis results for the measurement
            const result: PsychoAcousticsResults = await soundProcessingService.getPsychoAcoustics(
                this.measurements[measurementIdx],
                { requestedMicrophone: mic },
                (progress: number) => this.setSubProgress(measurementIdx, progress)
            );

            if (!result?.data) {
                throw new Error("Invalid response: missing data field");
            }

            this.computedResults[measurementId] = {} as PsychoAcousticAnalysisResult;
            for (const metricType of AllPsychoAcousticMetricTypes) {
                this.computedResults[measurementId][metricType] = result.data[Mapper[metricType]];
            }

            this.setSubProgress(measurementIdx, 1);
            return this.computedResults[measurementId];
        } catch (error) {
            console.error(error);
            // TODO-SUS-514: Use workflowmoduleinstance to automatically show thrown errors as toast
            throw {
                title: `Error processing measurement '${name}'`,
                message: "Please select a different measurement or contact Sorama.",
                name: "AnalysisError",
            } as IToastError;
        }
    }

    // Makes sure this.measurements reflects this.selectedMeasurementIds
    private async syncMeasurements(): Promise<void> {
        // Filter everything from this.measurements that is not in selectedMeasurementIds
        const remainingMeasurements = this.measurements.filter((m) => this.selectedMeasurementIds.includes(m.id));

        // Fetch everything that is not in this.measurements
        const newMeasurementPromises: Promise<GenericMeasurement>[] = this.selectedMeasurementIds
            .filter((id) => !this.measurements.find((m) => m.id === id))
            .map((id) => this.measurementRepository.getMeasurement(id));

        const newMeasurements = await Promise.all(newMeasurementPromises);
        const measurements = [...remainingMeasurements, ...newMeasurements];

        // Make sure the ordering is teh same as in selectedMeasurementIds
        this.measurements = this.selectedMeasurementIds.map((id) => measurements.find((m) => m.id === id));
    }

    private setMeasurementObservers(measurement: GenericMeasurement): void {
        this.colorObserverDisposables.push(
            this.measurementColorService.subscribe({ measurementId: measurement.id }, (color) => {
                // color picker updates when the array is updated. Little deeper than shallow copy
                this.currentUnitSpectra = <UnitSpectrumWithSelectedMeasurement[]>this.currentUnitSpectra.map((a) => {
                    return { ...a };
                });
                this.updateSpectra();
            })
        );

        if (measurement.isComputedMeasurement) {
            // Also add an observer to the source
            this.measurementUpdateDisposables.push(
                this.measurementRepository.measurementUpdateObserver(
                    this.measurementUpdated,
                    measurement.computedMeasurementSourceId
                )
            );
        }

        this.measurementUpdateDisposables.push(
            this.measurementRepository.measurementUpdateObserver(this.measurementUpdated, measurement.id)
        );
    }

    private async getSpectra(
        measurement: GenericMeasurement,
        setProgress: (progress: number) => void = () => {}
    ): Promise<UnitSpectrumWithSelectedMeasurement> {
        // Check if we already have this id
        if (this.currentUnitSpectra) {
            const spectrum = this.currentUnitSpectra.find(
                (x) =>
                    x.measurement.id === measurement.id &&
                    (this.variant || x.usedComputationMode === this.computationMode) && // only relevant for non-variant
                    (!this.variant || x.selectedMic === this.selectedMic) // only relevant for variant
            );
            if (spectrum) {
                // Already had this one, remove from the progress set
                setProgress(undefined);
                return Promise.resolve(spectrum);
            }
        }

        const unitSpectrumWithMeasurement = await this.spectrumProvider.getSpectra(
            measurement,
            this.selectedMic, // Setting mic to null for normal spectrum will give summed result for all microphones
            this.computationMode,
            setProgress
        );

        return unitSpectrumWithMeasurement;
    }

    private setMaxXAxisValue(): void {
        if (this.variant) {
            // For variants (psychoacoustic metrics), use the predefined critical band bounds
            this.maxDisplayedXAxisValue = this.defaultBoundsCriticalBands.to;
            this.maxXAxis = this.maxDisplayedXAxisValue.value;
            return;
        }

        // Base the max frequency on the spectrum resolution
        let newMaxValue = 1e6;
        for (let i = 0; i < this.currentUnitSpectra.length; i++) {
            const spectrum = this.currentUnitSpectra[i];
            newMaxValue = Math.min(spectrum.data[spectrum.data.length - 1].x, newMaxValue);
        }

        this.maxDisplayedXAxisValue = new UnitValue(newMaxValue, this.displayXAxisUnit);
        this.maxXAxis = this.maxDisplayedXAxisValue.value;

        // If iV64 our correction curve only goes up to 22387 Hz, we set the X axis to only show up to that frequency
        // But since maximumDisplayedXUnitValue is set to nyquist frequency, which is higher, if needed a user can show more
        // Note: this might be temporary until our correction curve extends to higher frequencies
        if (this.computationMode === ComputationModes.iV64) {
            this.maxXAxis = ComputationModes.getValidFrequencyRange(ComputationModes.iV64).to;
        }
    }

    private updateRecommendation(measurements: GenericMeasurement[]): void {
        this.recommendedFreqRange = new RecommendationEngine()
            .forMeasurements(measurements)
            .getFrequencyBandRecommendations()
            .map((x) => {
                return <PlotRange>{
                    from: new UnitValue(x.from, Frequency.hertz),
                    to: new UnitValue(x.to, Frequency.hertz),
                };
            });
    }

    private computeSpectra(legend?: boolean): void {
        if (!this.currentUnitSpectra) {
            this.spectra = [];
            return;
        }

        if (
            this.currentUnitSpectra?.length &&
            this.workflowModeService.selectedWorkflowMode.isIndustrial &&
            this.frequencySelectionService.ranges.length == 0
        ) {
            const device =
                this.measurements[this.currentUnitSpectra.length - 1]?.specialMeasurementInfo?.metadata?.instrument
                    ?.type;

            const suggestedRanges = this.frequencySuggestionService.createSuggestedRanges(
                this.currentUnitSpectra[this.currentUnitSpectra.length - 1],
                device,
                this.presetType
            );

            this.frequencySelectionService.ranges.splice(
                0,
                this.frequencySelectionService.ranges.length,
                ...suggestedRanges
            );

            this.suggestionsInitialized = true;
        }

        this.currentWeightedSpectra = this.currentUnitSpectra.map((spectrum: UnitSpectrumWithMeasurement) => {
            if (this.currentWeightedSpectra) {
                // Look up existing to prevent recomputed
                const existing = this.currentWeightedSpectra.find(
                    (dbWeighted) =>
                        dbWeighted.measurement.id === spectrum.measurement.id &&
                        dbWeighted.weighting.equals(this.selectedUnit.weighting) &&
                        dbWeighted.xUnit === this.selectedFrequencyAxisKind.unit &&
                        dbWeighted.yUnit === this.displayYAxisUnit &&
                        dbWeighted.weightingLowerBound.value === this.weightingLowerBound.value
                );

                if (existing) return existing;
            }

            const weighting = this.variant ? new NoneWeighting() : new ZWeighting();
            const weightingLowerBound = this.variant
                ? new UnitValue(1, PsychoAcousticBandUnits[this.variant])
                : new UnitValue(22, Frequency.hertz);

            const weightedSpectrum = new WeightedSpectrumWithMeasurement(
                weighting,
                weightingLowerBound,
                spectrum.xUnit,
                spectrum.yUnit,
                spectrum.data,
                spectrum.properties.timeRange
            );

            // Critical band doesn't need to be converted to dbWeighted --> Early exit
            if (this.variant) {
                weightedSpectrum.measurement = spectrum.measurement;
                weightedSpectrum.usedComputationMode = spectrum.usedComputationMode;
                return weightedSpectrum;
            } else {
                // For frequency spectrum we want to convert to dB
                weightedSpectrum.weighting = this.selectedUnit.weighting;
                weightedSpectrum.weightingLowerBound = this.weightingLowerBound;

                const dbWeightedSpectrum = <WeightedSpectrumWithMeasurement>(
                    weightedSpectrum.convertToUnits(this.selectedFrequencyAxisKind.unit, this.displayYAxisUnit)
                );

                dbWeightedSpectrum.measurement = spectrum.measurement;
                dbWeightedSpectrum.usedComputationMode = spectrum.usedComputationMode;

                return dbWeightedSpectrum;
            }
        });

        this.updateSpectra();

        if (!legend && !this.showLegend) this.showLegend = this.spectra.length > 1;

        this.activeRangesChanged();
        this.frequencySelectionService.registerModule(this.uniqueModuleId, this.spectra);
    }

    private updateSpectra() {
        // Show
        this.spectra = this.currentWeightedSpectra.map((dbWeightedSpectrum, index) => {
            const color = this.measurementColorService.getColor({ measurementId: dbWeightedSpectrum.measurement.id })
                .color.css;

            return new LineData(
                dbWeightedSpectrum.data,
                dbWeightedSpectrum.xUnit,
                dbWeightedSpectrum.yUnit,
                dbWeightedSpectrum.weighting,
                {
                    name: dbWeightedSpectrum.measurement.name,
                    color: color,
                    opacity: 1.0,
                    displayValue: undefined,
                    displayValueColor: undefined,
                    id: dbWeightedSpectrum.measurement.id,
                }
            );
        });

        this.updatePowerValue();

        this.updateBands();
        this.updateLegendArray();
    }

    private updateBands(): void {
        // Critical band mode doesn't use bands.
        if (this.variant) {
            this.updateExtrema();
            return;
        }

        // Clear all bar plots
        for (const barPlot of this.spectra.filter((x) => x.type === PlotDataTypes.BarData)) {
            const index = this.spectra.findIndex((x) => x === barPlot);
            if (index != -1) this.spectra.splice(index, 1);
        }

        // Add octave bands for each selected measurement.
        for (let i = 0; i < this.currentUnitSpectra.length; i++) {
            // Add octave bands
            if (
                this.currentUnitSpectra[i].selectedFrequencyBandKind != FrequencyBandKinds.None &&
                this.currentUnitSpectra[i].selectedFrequencyBandId
            ) {
                const currentUnitSpectrum = this.currentUnitSpectra.find(
                    (x) => x.measurement.id === this.currentUnitSpectra[i].selectedFrequencyBandId
                );

                if (!currentUnitSpectrum) return;

                // Calculate Octave Bands
                const deltaf = currentUnitSpectrum.data[1].x - currentUnitSpectrum.data[0].x;

                const spectrumFrequencyBandValues = currentUnitSpectrum.data.map(
                    (x) => new BandValue(new LinearFrequencyBand(x.x, deltaf), x.y)
                );

                const octaveBandValues =
                    this.currentUnitSpectra[i].selectedFrequencyBandKind.toBands(spectrumFrequencyBandValues);

                const octaveBandUnitSpectrum = new UnitSpectrum(
                    currentUnitSpectrum.xUnit,
                    currentUnitSpectrum.yUnit,
                    octaveBandValues.map((x) => <Point2D>{ x: x.band.center, y: x.value }),
                    currentUnitSpectrum.properties.timeRange
                );

                // Apply weighting
                const weightedOctaveBandSpectrum = WeightedSpectrum.FromUnitSpectrum(
                    new ZWeighting(),
                    octaveBandUnitSpectrum
                );
                weightedOctaveBandSpectrum.weighting = this.selectedUnit.weighting;

                // Convert to db
                const dbWeightedOctaveBandSpectrum = weightedOctaveBandSpectrum.convertToUnits(
                    this.selectedFrequencyAxisKind.unit,
                    this.displayYAxisUnit
                );

                const currentMeasurement: GenericMeasurement = this.currentUnitSpectra.find(
                    (x) => x.measurement.id === this.currentUnitSpectra[i].selectedFrequencyBandId
                ).measurement;

                const boundaryForBar: Boundary[] = octaveBandValues.map(
                    (x) => <Boundary>{ lower: x.band.lower, upper: x.band.upper }
                );

                // Show
                this.spectra.unshift(
                    new BarData(
                        dbWeightedOctaveBandSpectrum.data,
                        dbWeightedOctaveBandSpectrum.xUnit,
                        dbWeightedOctaveBandSpectrum.yUnit,
                        dbWeightedOctaveBandSpectrum.weighting,
                        <BarDataDisplayOptions>{
                            barMargin: 0.001,
                            barBottomLocation: BarBottomLocation.GraphBottom,
                            barLabel: (point, idx) =>
                                OctaveBands.toHumanReadableBandFrequency(
                                    this.displayXAxisUnit.fromUnit(dbWeightedOctaveBandSpectrum.xUnit, point.x)
                                ),
                            barLabelLocation: BarTextLocation.GraphBottom,
                            displayValue: (point, idx) => dbWeightedOctaveBandSpectrum.data[idx].y.toFixed(1),
                            displayValueLocation: BarTextLocation.BarTop,
                            name:
                                currentMeasurement.name +
                                ": " +
                                this.currentUnitSpectra[i].selectedFrequencyBandKind.name +
                                " Bands",
                            id: "_" + currentMeasurement.id,
                            barType: this.measurementColorService.getColor({ measurementId: currentMeasurement.id })
                                .color.css, //i % 2 == 0 ? "Primary" : "Secondary"
                        },
                        boundaryForBar
                    )
                );
            }
        }

        this.updateExtrema();
    }

    private updateExtrema(): void {
        if (!this.autoMinScaling && !this.autoMaxScaling) return;

        // Determine min and max values
        let minValue = Number.POSITIVE_INFINITY;
        let maxValue = Number.NEGATIVE_INFINITY;

        // Get defined bounds
        const defaultBounds = this.variant ? this.defaultBoundsCriticalBands : this.defaultBoundsFrequency;

        // Do not use frequencies below defined bounds for showing frequencies.
        const lowerBound = this.variant
            ? defaultBounds.from.value
            : defaultBounds.from.toUnit(this.selectedFrequencyAxisKind.unit);

        for (const spectrum of this.spectra) {
            // Omit values below lower bounds
            for (let i = 0; i < spectrum.data.length; i++) {
                const point = spectrum.data[i];
                if (point.x < lowerBound) continue;

                const val = point.y;
                minValue = Math.min(val, minValue);
                maxValue = Math.max(val, maxValue);
            }
        }

        if (this.spectra && this.spectra.length > 0) {
            const intervalLength = maxValue - minValue;
            const spacingPercentage = 0.1;
            const spacing = intervalLength * spacingPercentage;

            // Define boundary: 10% (spacingPercentage), spacing around the lowest and highest value of the plot
            const upperPlotBound = maxValue + spacing;

            // For critical band value we prevent min ylim going below 0 as that does not make sense
            const lowerPlotBound = this.variant ? Math.max(0, minValue - spacing) : minValue - spacing;

            if (this.autoMaxScaling) {
                if (!this.maxYAxis || Math.abs(this.maxYAxis - upperPlotBound) > 1e-5) {
                    this.ignoreMaxYUpdate = true;
                    this.maxYAxis = upperPlotBound;
                }
            }

            if (this.autoMinScaling) {
                if (!this.minYAxis || Math.abs(this.minYAxis - lowerPlotBound) > 1e-5) {
                    this.ignoreMinYUpdate = true;
                    this.minYAxis = lowerPlotBound;
                }
            }
        }
    }
    //#endregion

    //#region Button callbacks

    public exportData(): void {
        const state = {
            spectra: this.spectra,
            selectedFrequencyAxisKind: this.selectedFrequencyAxisKind,
            selectedWeighting: this.selectedUnit.weighting.name,
        };

        this.csvState.state = state;
        this.csvState.moduleInstance = this.moduleInstance;
    }

    public exportVisual(): ImageExportState {
        const exportModalState = {
            measurementIds: this.selectedMeasurementIds,
            exportable: this.dataExportable,
            isLegendDisplayed: true,
            initialLegendState: this.showLegend,
            showLegend: (key: boolean) => {
                if (key !== undefined) {
                    this.showLegend = key;
                }
                return this.showLegend;
            },
            initialFileName:
                this.measurements && this.measurements.length > 0 ? this.measurements[0].name + "_Spectrum" : undefined,
            initialLogoPosition: LogoPositions.bottomRight,
            namedProperties: [
                { name: "[Weighting]", value: this.selectedUnit.weighting.name + " Weighting" },
                {
                    name: "[Frequencies]",
                    value: FileNameConversion.convertSelectedFrequencies(this.frequencySelectionService.ranges),
                },
                { name: "[Scale]", value: this.selectedFrequencyAxisKind.name + " Scale" },
            ],
        };
        this.imageState.state = exportModalState;
        this.imageState.moduleInstance = this.moduleInstance;

        return exportModalState;
    }

    public linkFrequencyRange(link: boolean): void {
        this.isFrequencyRangeLinked = link;
    }

    public setAutomaticMinScaling(auto: boolean): void {
        this.autoMinScaling = auto;

        if (this.autoMinScaling) {
            this.updateExtrema();
        }
    }

    public setAutomaticMaxScaling(auto: boolean): void {
        this.autoMaxScaling = auto;

        if (this.autoMaxScaling) {
            this.updateExtrema();
        }
    }

    //#endregion

    //#region Helper methods

    protected async openFarFieldModuleForCriticalBand(): Promise<void> {
        for (const measurement of this.measurements) {
            await AnalysisWorkflowModuleHelpers.ensureModuleOpenForMeasurement(
                this.moduleInstance.getWorkflow(),
                measurement.id,
                AnalysisWorkflowModuleCatalog.FarfieldModule
            );
        }
    }

    private updateNarrowBandSelectionMode(): void {
        // Critical Bands does not use narrow bands
        if (this.variant) return;

        this.frequencySelectionService.setSelectionMode(this.narrowBandSelectionMode);
    }

    private updateLegendArray() {
        this.legendArray = [
            {
                entries: this.currentWeightedSpectra.map((x): LegendEntry => {
                    if (!x.measurement.name) {
                        return;
                    }
                    return {
                        name: x.measurement.name,
                        color: this.measurementColorService.getColor({ measurementId: x.measurement.id }).color.css,
                    };
                }),
            },
        ];
    }

    private updatePowerValue(): void {
        // Critical bands don't do power values
        if (this.variant) return;

        this.powerValue = [];
        this.powerValueView = [];
        let power: number;
        const validRange = ComputationModes.getValidFrequencyRange(this.computationMode);

        this.currentWeightedSpectra.map((d) => {
            power = d.toPowerValue(d.yUnit, validRange).toUnit(this.displayYAxisUnit);
            this.powerValue.push(power);

            // save it for report
            this.powerValueView.push({
                name: d.measurement.name,
                powerValue: power,
                color: this.measurementColorService.getColor({ measurementId: d.measurement.id }).color.css,
                weighting: this.selectedUnit.weighting.name,
            });
        });

        this.splMessage = `The ${this.displayYAxisUnit.unit} for ${validRange.from} Hz to ${validRange.to} Hz.`;
    }

    public openSpectrumSettingsModal(): void {
        if (this.spectrumSettingsModal && this.spectrumSettingsModal.isOpen) {
            this.spectrumSettingsModal.cancel();
            this.spectrumSettingsModal = undefined;
            return;
        }
        void this.moduleInstance
            .createModal<SettingsModalState<SpectrumModule>, void>(WorkflowModalCatalog.SpectrumSettingsModal)
            .withState({ belongsTo: this })
            .whenClosed(() => {
                this.spectrumSettingsModal = undefined;
            })
            .show()
            .then((x) => {
                this.spectrumSettingsModal = x;
            });
    }

    public openExportModal(): void {
        if (this.exportModal && this.exportModal.isOpen) {
            this.exportModal.cancel();
            this.exportModal = undefined;
            return;
        }
        this.exportData();
        this.exportVisual();

        void this.moduleInstance
            .createModal<ExportModalState<IExportModule>, void>(WorkflowModalCatalog.ExportModal)
            .withState({ belongsTo: this })
            .whenClosed(() => {
                this.exportModal = undefined;
            })
            .show()
            .then((x) => {
                this.exportModal = x;
            });
    }
    //#endregion
}
